import { useState } from 'react'
import clsx from 'clsx'
import { Layers } from 'lucide-react'
import { useFleet } from '../state/store'
import { RASTER_BASEMAPS, type BasemapMode } from '../map/basemaps'

/**
 * Which basemap sits under the fleet.
 *
 * Base only. The heat overlays moved to MapModeSwitch, which drives `heatMode` from the
 * 1a/1b/1c selection -- a second heat control here would set the same store field from two
 * places and the last writer would win, silently.
 *
 * Collapsed to a chip by default. It sits in the bottom-right corner where the full row of
 * six options would be ~590 px of map covered to serve a control that is touched once a
 * session; the chip still names the active basemap, so the answer to "what am I looking
 * at" costs no clicks even when the list is shut.
 *
 * Ordering is the argument the catalogue makes: the offline vector archive first because it
 * is the default and the only one that survives a disconnected depot, the remote rasters
 * after it, photoreal last because it is billed. `needs internet` is stated rather than
 * implied -- on a machine with no route out, switching away from vector produces a blank
 * map and no error, and the operator needs to know which switch did that.
 */
export function BasemapSwitch(): JSX.Element {
  const basemap = useFleet((s) => s.basemapMode)
  const setBasemap = useFleet((s) => s.setBasemapMode)
  const photorealAvailable = useFleet((s) => s.photorealAvailable)
  const google2dAvailable = useFleet((s) => s.google2dAvailable)
  const attribution = useFleet((s) => s.attribution)
  const [open, setOpen] = useState(false)

  const remote = true
  const activeLabel = basemap === 'photoreal'
      ? 'photoreal 3D'
      : basemap === 'google2d'
        ? 'google maps'
        : RASTER_BASEMAPS.find((b) => b.id === basemap)?.label ?? basemap

  const pill = (
    id: BasemapMode, label: string, title: string,
    opts: { disabled?: boolean; billed?: boolean } = {},
  ): JSX.Element => {
    const active = basemap === id
    return (
      <button
        key={id}
        type="button"
        title={title}
        aria-pressed={active}
        disabled={opts.disabled}
        onClick={() => setBasemap(id)}
        className={clsx(
          'rounded-[8px] px-2 py-1 text-[11px] font-semibold transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]',
          'disabled:cursor-not-allowed disabled:opacity-40',
          active
            ? opts.billed ? 'bg-[#D97706] text-white' : 'bg-[#0F172A] text-white'
            : 'text-[#475569] hover:bg-[rgba(15,23,42,0.05)]',
        )}
      >
        {label}
      </button>
    )
  }

  // Collapsed: a chip naming the active basemap. Expands upward, because the control sits
  // on the bottom edge and a list that grew downward would open off the map.
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-expanded={false}
        title="Change the basemap"
        className="flex items-center gap-1.5 rounded-[14px] border border-[rgba(15,23,42,0.07)] bg-white/85 px-2.5 py-1.5 shadow-[0_2px_8px_rgba(15,23,42,0.08)] backdrop-blur-sm transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]"
      >
        <Layers className="h-3.5 w-3.5 shrink-0 text-[#64748B]" />
        <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-[#94A3B8]">
          Base
        </span>
        <span className="text-[11px] font-semibold text-[#0F172A]">{activeLabel}</span>
      </button>
    )
  }

  return (
    <div className="rounded-[14px] border border-[rgba(15,23,42,0.07)] bg-white/85 p-1 shadow-[0_2px_8px_rgba(15,23,42,0.08)] backdrop-blur-sm">
      <div className="flex flex-wrap items-center justify-end gap-0.5">
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-expanded
          title="Hide the basemap list"
          className="flex items-center gap-1.5 rounded-[8px] px-1.5 py-1 transition-colors hover:bg-[rgba(15,23,42,0.05)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]"
        >
          <Layers className="h-3.5 w-3.5 shrink-0 text-[#64748B]" />
          <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-[#94A3B8]">
            Base
          </span>
        </button>

        {/* Google Maps first: it is the default (no self-hosted 'vector' option any more,
            request 2026-11-04). The remote rasters follow as alternatives. */}
        {pill('google2d', 'google maps',
          google2dAvailable
            ? 'Google Maps roadmap tiles (Map Tiles API). The default. Billed per session, needs internet.'
            : 'Set GOOGLE_MAPS_API_KEY on the serving service to enable.',
          { disabled: !google2dAvailable, billed: true })}

        {RASTER_BASEMAPS.map((b) => pill(b.id, b.label, b.hint))}

        {pill('photoreal', 'photoreal 3D',
          photorealAvailable
            ? 'Google Photorealistic 3D Tiles. Billed per session, needs internet. Right-drag to tilt.'
            : 'Set GOOGLE_MAPS_API_KEY on the serving service to enable.',
          { disabled: !photorealAvailable, billed: true })}
      </div>

      {remote && (
        <div className="px-1.5 pb-0.5 pt-1 text-right text-[10px] text-[#94A3B8]">
          {basemap === 'photoreal' ? 'billed · right-drag to tilt · needs internet'
            : basemap === 'google2d' ? 'billed · needs internet' : 'needs internet'}
        </div>
      )}

      {/* Google requires the returned copyright strings to be displayed. Licence
          attribution for the raster services rides on the source itself and is rendered by
          MapLibre's own attribution control. */}
      {basemap === 'photoreal' && attribution.length > 0 && (
        <div className="max-w-[420px] truncate px-1.5 pb-1 text-right text-[10px] text-[#94A3B8]"
             title={attribution.join(' · ')}>
          {attribution.join(' · ')}
        </div>
      )}
    </div>
  )
}
